import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/firebaseServices';
import './blogcss/blog.css';

// Local images
import morningRoutineImage from './blogimg/morningRoutine/transformation.jpg';
import stimulation from './blogimg/morningRoutine/stimulation.jpg';
import habits from './blogimg/morningRoutine/habits.jpg';
import meditationImage from './blogimg/morningRoutine/meditation.jpg';
import stretchingImage from './blogimg/morningRoutine/stretching.jpg';
import breakfastImage from './blogimg/morningRoutine/breakfast.jpg';
import herbalTeaImage from './blogimg/morningRoutine/herbalTea.jpg';
import smartGoalsImage from './blogimg/morningRoutine/smartGoals.jpg';
import ashwagandhaImage from './blogimg/morningRoutine/ashwagandha.jpg';

/**
 * Maps the provided filename from Firestore to the corresponding local image import.
 */
function getLocalImageSource(filename) {
  const commaIndex = filename.indexOf(',');
  if (commaIndex !== -1) {
    filename = filename.substring(0, commaIndex);
  }
  const parts = filename.split(/[/\\]/);
  const baseName = parts[parts.length - 1].trim().toLowerCase();

  switch (baseName) {
    case 'transformation.jpg':
      return morningRoutineImage;
    case 'stimulation.jpg':
      return stimulation;
    case 'habits.jpg':
      return habits;
    case 'meditation.jpg':
      return meditationImage;
    case 'stretching.jpg':
      return stretchingImage;
    case 'breakfast.jpg':
      return breakfastImage;
    case 'herbaltea.jpg':
      return herbalTeaImage;
    case 'smartgoals.jpg':
      return smartGoalsImage;
    case 'ashwagandha.jpg':
      return ashwagandhaImage;
    default:
      return null;
  }
}

const CalmingMorningRoutinePage = () => {
  const [article, setArticle] = useState(null);
  const [timestamp, setTimestamp] = useState(null);

  // 1️⃣ Add Meta Tags Dynamically
  useEffect(() => {
    // Set the document title
    document.title = "Calming Morning Routine: Simple Ways to Start Your Day at Peace";

    // Helper function to add/update a meta tag
    const setMetaTag = (attrName, contentValue, useProperty = false) => {
      const key = useProperty ? 'property' : 'name';
      let metaTag = document.querySelector(`meta[${key}="${attrName}"]`);

      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute(key, attrName);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', contentValue);
    };

    // Standard meta tags
    setMetaTag(
      "description",
      "Discover a calming morning routine to reduce stress, enhance emotional well-being, and set a peaceful tone for the day. Explore mindful practices, herbal teas, and tips for lasting tranquility."
    );
    setMetaTag(
      "keywords",
      "calming morning routine, stress relief, emotional well-being, mindfulness, herbal tea, ashwagandha, Pure Health Nexus"
    );

    // Open Graph tags
    setMetaTag(
      "og:title",
      "Calming Morning Routine: Start Your Day at Peace",
      true
    );
    setMetaTag(
      "og:description",
      "Learn how to establish a calming morning routine with meditation, gentle stretching, mindful eating, and supplement support to stay balanced all day.",
      true
    );
    setMetaTag(
      "og:image",
      "https://purehealthnexus.com/assets/images/transformation.jpg",
      true
    );
    setMetaTag(
      "og:url",
      "https://purehealthnexus.com/calming-morning-routines",
      true
    );

    // Twitter card
    setMetaTag("twitter:card", "summary_large_image");

    // Optional cleanup on unmount
    return () => {
      document.title = "Pure Health Nexus";
    };
  }, []);

  // 2️⃣ Fetch Data from Firestore
  useEffect(() => {
    const fetchArticle = async () => {
      try {
        // Firestore path: /products/blog/ashwagandha/
        const colRef = collection(db, 'products', 'blog', 'ashwagandha');
        const snapshot = await getDocs(colRef);

        if (!snapshot.empty) {
          const docData = snapshot.docs[0].data();
          setArticle(docData);
          setTimestamp(docData.eutimestamp);
        } else {
          // console.warn('No blog document found for ashwagandha');
        }
      } catch (error) {
        // console.error('Error fetching blog article:', error);
      }
    };

    fetchArticle();
  }, []);

  if (!article) {
    return <div>Loading...</div>;
  }

  // 3️⃣ Render Block Function
  const renderBlock = (block, index) => {
    const { tag, text } = block;
    switch (tag) {
      case 'h1':
        return (
          <h1 key={index} className="recipe-h1">
            {text}
            <style>{`.recipe-h1::after { left: 8%; }`}</style>
          </h1>
        );
      case 'h2':
        return (
          <React.Fragment key={index}>
            <hr
              style={{
                width: '75%',
                margin: '20px auto',
                border: '1px solid #e67e00',
              }}
            />
            <h2 className="recipe-h2">
              <span>{text}</span>
            </h2>
          </React.Fragment>
        );
      case 'h3':
        return (
          <React.Fragment key={index}>
            <hr
              style={{
                width: '75%',
                margin: '20px auto',
                border: '1px solid #e67e00',
              }}
            />
            <h3 className="recipe-h3">{text}</h3>
          </React.Fragment>
        );
      case 'h4':
        return <h4 key={index}>{text}</h4>;
      case 'p':
        return (
          <p key={index} className="recipe-p">
            {text}
          </p>
        );
      case 'img': {
        const localImg = getLocalImageSource(text);
        return (
          <div key={index} className="recipe-img">
            <img
              src={localImg ?? text}
              alt={text}
              style={{ maxHeight: '500px', width: 'auto' }}
            />
          </div>
        );
      }
      case 'ul':
        return (
          <ul key={index} className="recipe-ul">
            {Array.isArray(text)
              ? text.map((li, liIndex) => <li key={liIndex}>{li}</li>)
              : <li>{text}</li>}
          </ul>
        );
      case 'ol':
        return (
          <ol key={index} className="recipe-ol">
            {Array.isArray(text) &&
              text.map((li, liIndex) => {
                if (typeof li === 'object') {
                  return (
                    <li key={liIndex}>
                      {li.text}
                      {li.nested && (
                        <ul>
                          {li.nested.map((nestedItem, nestedIndex) => (
                            <li key={nestedIndex}>{nestedItem}</li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                } else {
                  return <li key={liIndex}>{li}</li>;
                }
              })}
          </ol>
        );
      case 'hr':
        return (
          <hr
            key={index}
            style={{
              width: '75%',
              margin: '20px auto',
              border: '1px solid #e67e00',
            }}
          />
        );
      default:
        return <div key={index}>{text}</div>;
    }
  };
  // Format the timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };
  // 4️⃣ Render the Page
  return (
    <div className="container">
      <div className="calming-morning-routine">
        {/* Main H1 from Firestore data */}
        {article.h1 && (
          <h1 className="recipe-h1" style={{ position: 'relative' }}>
            {article.h1}
            <style>{`.recipe-h1::after { left: 8%; }`}</style>
          </h1>
        )}
        {timestamp && (
          <p className="timestamp" style={{ textAlign: 'center', color: '#888' }}>
            Updated: {formatTimestamp(timestamp)}
          </p>
        )}
        {/* Render each section of the article */}
        {article.sections &&
          article.sections.map((section, sectionIndex) => (
            <section key={sectionIndex}>
              {section.blocks &&
                section.blocks.map((block, blockIndex) =>
                  renderBlock(block, blockIndex)
                )}
            </section>
          ))}

        {/* CTA Button */}
        <button
          className="recipe-button"
          onClick={() => (window.location.href = 'https://purehealthnexus.com/ashwagandha')}
        >
          See Ashwagandha Here
        </button>
      </div>
    </div>
  );
};

export default CalmingMorningRoutinePage;
